'use client';

import { type ReactNode, useEffect, useRef } from 'react';

import type { AbstractIntlMessages } from 'next-intl';
import { NextIntlClientProvider } from 'next-intl';

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * A message key the provider was asked for and could not resolve.
 */
export interface MissingMessageReport {
  /** Namespace the lookup was scoped to, when the caller supplied one. */
  namespace: string | undefined;
  /** Key within that namespace. */
  key: string;
  /** `namespace.key` — the identity used to de-duplicate reports. */
  path: string;
  locale: string;
  /** next-intl `IntlErrorCode`, e.g. `MISSING_MESSAGE`. */
  code: string;
}

interface I18nClientProviderProps {
  locale: string;
  /**
   * Messages handed to the client tree.
   *
   * Optional on purpose. Every value a server component passes to a client
   * component is serialised into the RSC flight payload embedded in the HTML,
   * so a route should hand over only the namespaces it renders. next-intl
   * resolves an absent namespace through `getMessageFallback` rather than
   * throwing, and `app/global-error.tsx` already relies on this by passing
   * `{}`.
   */
  messages?: AbstractIntlMessages;
  children: ReactNode;
  timeZone?: string;
  /**
   * Called once per distinct missing key.
   *
   * Narrowing the message payload per route trades bytes for the risk of
   * omitting a namespace a route actually renders — and that failure is
   * otherwise invisible, because an unresolved key renders as its own text.
   * Wire this to monitoring so the gap arrives as a signal instead of as
   * placeholder text on a customer's screen.
   */
  onMissingMessage?: (report: MissingMessageReport) => void;
}

/**
 * Client-side provider for next-intl.
 * Wraps the application and provides translation context to all client components.
 */
export function I18nClientProvider({
  locale,
  messages,
  timeZone = 'UTC',
  onMissingMessage,
  children,
}: I18nClientProviderProps) {
  // `getMessageFallback` runs during render, so reports are queued here and
  // flushed from an effect rather than fired mid-render.
  const pendingRef = useRef<MissingMessageReport[]>([]);
  const reportedRef = useRef<Set<string> | null>(null);

  // Flush on every commit: a miss can surface while rendering a child long
  // after the provider itself has mounted. The flush is a no-op when nothing
  // is queued.
  useEffect(() => {
    if (pendingRef.current.length === 0) {
      return;
    }

    const batch = pendingRef.current;
    pendingRef.current = [];

    reportedRef.current ??= new Set<string>();
    const reported = reportedRef.current;

    for (const report of batch) {
      // A missing key stays missing for the life of the page while every
      // re-render asks for it again. Report each distinct path once so
      // monitoring gets a signal rather than a flood.
      if (reported.has(report.path)) {
        continue;
      }

      reported.add(report.path);
      reportMissingMessage(report, onMissingMessage);
    }
  });

  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      timeZone={timeZone}
      getMessageFallback={(info) => {
        const path = info.namespace
          ? `${info.namespace}.${info.key}`
          : info.key;

        // Queued from here rather than from `onError` because this is the only
        // hook next-intl gives us that carries the namespace and key as
        // structured values — `IntlError` only has a prose message.
        pendingRef.current.push({
          namespace: info.namespace,
          key: info.key,
          path,
          locale,
          code: info.error.code,
        });

        // simply fallback to the key as is
        return info.key;
      }}
      onError={(error) => {
        if (isDevelopment) {
          // Missing translations are expected and should only log an error
          console.warn(`[Dev Only] i18n error: ${error.message}`);
        }
      }}
    >
      {children}
    </NextIntlClientProvider>
  );
}

function reportMissingMessage(
  report: MissingMessageReport,
  onMissingMessage: ((report: MissingMessageReport) => void) | undefined,
) {
  if (onMissingMessage) {
    onMissingMessage(report);

    return;
  }

  // No handler wired. Never stay silent: an unresolved key is rendered to the
  // user verbatim, so it must at minimum reach the console in production too.
  const message = `[i18n] missing message "${report.path}" for locale "${report.locale}" (${report.code})`;

  if (isDevelopment) {
    console.warn(message);
  } else {
    console.error(message);
  }
}
